import { CUP_RADIUS_FT, FT_PER_M } from './puttMath';

/** The accepted putt a host hands the scene (PuttSceneInput on the phone,
 *  the TV putt event on the page). */
export interface ReplayPuttInput {
  /** Measured entry speed; if omitted, derived from distance + stimp. */
  speedMps?: number;
  distanceFt: number;
  angleDeg: number | null;
  proximityFt: number | null;
  /** Authoritative make verdict if known; else derived from proximity. */
  holed?: boolean;
}

export interface ReplayInputs {
  active: boolean;
  distanceFt: number;
  speedFtS: number;
  /** Solver angle: a null start line rolls dead straight. */
  angleDeg: number;
  /** HUD angle: keeps the null so a missing start line reads as '--'. */
  hudAngleDeg: number | null;
  proximityFt: number | null;
  made: boolean;
}

/**
 * The replay inputs both screens derive from an accepted putt (moved out of
 * the phone's LocalPuttScene, 2026-09-25, so the TV cannot drift): measured
 * entry speed when present, else the speed that stops at the measured
 * distance under a stimp-style decel (same physics as decelFor); a null angle
 * rolls straight; the make verdict is the app's when known, else proximity
 * inside the cup radius; a pure-visual green (hideHole) never makes.
 */
export function puttReplayInputs(
  putt: ReplayPuttInput | null,
  stimp: number,
  hideHole: boolean
): ReplayInputs {
  const active = putt != null;
  const distanceFt = putt ? putt.distanceFt : 0;
  const speedFtS = !putt
    ? 0
    : putt.speedMps && putt.speedMps > 0.05
      ? putt.speedMps * FT_PER_M
      : Math.sqrt((36 / Math.max(stimp, 1)) * Math.max(distanceFt, 0.1));
  const angleDeg = putt ? putt.angleDeg ?? 0 : 0;
  const hudAngleDeg = putt ? putt.angleDeg : null;
  const proximityFt = putt ? putt.proximityFt : null;
  const made =
    hideHole || !putt
      ? false
      : putt.holed ?? (proximityFt != null && proximityFt <= CUP_RADIUS_FT);
  return { active, distanceFt, speedFtS, angleDeg, hudAngleDeg, proximityFt, made };
}
